'use client';

import {useState, useEffect, useContext, useRef} from 'react';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { InfoTooltip } from './info-tooltip';
import Stack from '@mui/material/Stack';
import { textColors } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import { useComponentLogger } from '@/lib/component-logger';
/**
 * Displays a small optional title at the top and smaller paragraph text underneath, usually as a description/footnote
 * @param {} param0 
 * @returns 
 */
export function ParagraphText({config}) {
    const logger = useComponentLogger("Paragraph Text")
    const { currentProject, selectedViewParams, prelimParamCount, changedParams } = useContext(DataContext);
    // logger.debug('para object:', textObject, selectedViewParams, prelimParamCount);
    const [tooltip, setTooltip] = useState(null);
    const [textContent, setTextContent] = useState(null);
    const [textTitle, setTextTitle] = useState(config.title || null);
    const [textColor, setTextColor] = useState('text.primary');
    const [contentLoading, setContentLoading] = useState(true);
    const trackedParamValsRef = useRef({});
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr

    // fetch and set content
    const fetchContent = async () => {
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;
        

        if (dataSource.type === "text") {
            setTextContent(config.dataSource.text);
            // logger.debug('para content', textObject.dataSource.text);
        } else if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('para api data', data.rows, data);

            if (data?.rows && data.rows.length > 0) {
                let apiConfig = data.rows[0];
                if (apiConfig.title) {
                    setTextTitle(apiConfig.title);
                }
                if (apiConfig.val) {
                    setTextContent(apiConfig.val);
                }
                if (apiConfig.color) {
                    if (textColors[apiConfig.color]) {
                        setTextColor(textColors[apiConfig.color]);
                    } else {
                        setTextColor('text.primary');
                    }
                }
            }
        }

        setContentLoading(false);
    }

    const processTooltip = async() => {
        let tooltipContent = await fetchTooltip(currentProject, selectedViewParams, config);
        if (tooltipContent) setTooltip(tooltipContent);
    }

    useEffect(() => {
        logger.debug('para config', config);
        let trackedParams = extractTrackedParams(config.dataSource);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;
        setContentLoading(true);
        fetchContent().catch(() => {});
        processTooltip().catch(() => {});
    }, []);


    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch, selectedViewParams);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
            processTooltip();
        }

        // make sure to clear the text content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!selectedViewParams[param]) {
                setTextContent(null);
                return;
            }
        }
    }, [changedParams]);

    if (textContent === null && !config.title) return null;

    return textContent ?  (
        <Stack spacing={2}>
            {textTitle ? 
                <Typography color="text.secondary" variant='h6' sx={{display: 'flex', alignItems: 'center'}}>
                    {textTitle}
                    {tooltip ? <InfoTooltip content={tooltip} /> : <></>}
                </Typography> 
                : <></>
            } 
            <Typography color={textColor} variant={"body1"} overflow={"wrap"}>
                { textContent || null}
            </Typography>
        </Stack>
    ) : <></>;
}